import { Tariff } from "../components/pricing/tariffs/utils/types";

/**
 * Generate a descriptive title for calendar events representing tariffs
 * 
 * @param tariff The tariff object containing details
 * @returns A formatted title string with route and transport information
 */
export const generateTariffCalendarTitle = (tariff: Tariff): string => {
  if (!tariff) return "Tariff Event";

  // Get origin and destination names
  const origin = tariff.origin_port_data?.name || "Any";
  const destination = tariff.destination_port_data?.name || "Any";
  
  // Get transport type
  const transport = tariff.transport_type_data?.name || "Any";
  
  // Get container size if available
  const containerSize = tariff.container_size_data?.name || "";
  
  // Get booking type if available
  const bookingType = tariff.bookings_type_data?.name || "";
  
  // Build the title with available information
  let title = `${origin} → ${destination}`;
  
  // Add transport type
  title += ` | ${transport}`;
  
  // Add container size and booking type if available
  const details = [containerSize, bookingType].filter(Boolean).join(", ");
  if (details) {
    title += ` | ${details}`;
  }
  
  // Add price if available
  if (tariff.price) {
    title += ` | $${tariff.price}`;
  }
  
  return title;
};

/**
 * Generate a shorter title for calendar events with limited space
 * 
 * @param tariff The tariff object containing details
 * @returns A concise formatted title string
 */
export const generateShortTariffCalendarTitle = (tariff: Tariff): string => {
  if (!tariff) return "Tariff";

  // Get origin and destination codes or short names
  const getShortName = (name?: string): string => {
    if (!name) return "";
    
    // If it's already short (3 chars or less), use it
    if (name.length <= 3) return name.toUpperCase();
    
    // Extract first letter of each word for abbreviation
    const words = name.split(/\s+/);
    if (words.length > 1) {
      return words.map(word => word.charAt(0).toUpperCase()).join("");
    }
    
    // Otherwise, just use the first 3 chars
    return name.substring(0, 3).toUpperCase();
  };
  
  const origin = getShortName(tariff.origin_port_data?.name);
  const destination = getShortName(tariff.destination_port_data?.name);
  
  // Get transport type abbreviation
  const getTransportAbbr = (transport?: string): string => {
    if (!transport) return "";
    
    if (transport.toLowerCase().includes("air")) return "AIR";
    if (transport.toLowerCase().includes("ocean")) return "SEA";
    if (transport.toLowerCase().includes("road")) return "RD";
    if (transport.toLowerCase().includes("rail")) return "RL";
    
    return transport.substring(0, 2).toUpperCase();
  };
  
  const transport = getTransportAbbr(tariff.transport_type_data?.name);
  
  // Build the concise title
  let title = `${origin}-${destination}`;
  
  if (transport) {
    title += `|${transport}`;
  }
  
  // Add container size if available (just the number)
  if (tariff.container_size_data?.name) {
    const sizeMatch = tariff.container_size_data.name.match(/\d+/);
    if (sizeMatch) {
      title += `|${sizeMatch[0]}'`;
    }
  }
  
  return title;
} 